import type { ShipmentWithCustomer, ShipmentWriteOff } from '@/api/types'

interface InvoicePromptDependencies {
  voidInvoice: (invoiceId: number) => Promise<unknown>
  confirm: (message: string) => boolean
}

export interface WriteOffDependencies extends InvoicePromptDependencies {
  writeOff: (shipmentId: number) => Promise<ShipmentWriteOff>
}

export interface CustomerReturnDependencies extends InvoicePromptDependencies {
  returnShipment: (shipmentId: number) => Promise<ShipmentWriteOff>
}

export interface WriteOffOutcome {
  shipment: ShipmentWithCustomer
  notice: string | null
}

const WRITTEN_OFF_TEXT = 'Leveransen är avskriven.'
const RETURNED_TEXT = 'Leveransen är markerad som returnerad av kunden.'

function formatInvoiceName(invoiceNumber: string | null): string {
  if (invoiceNumber === null) {
    return 'Fakturan'
  }

  return `Faktura ${invoiceNumber}`
}

async function askToVoidInvoice(
  result: ShipmentWriteOff,
  doneText: string,
  canVoidInvoices: boolean,
  dependencies: InvoicePromptDependencies
): Promise<WriteOffOutcome> {
  const { shipment, invoice } = result

  if (invoice === null) {
    return { shipment, notice: null }
  }

  const invoiceName = formatInvoiceName(invoice.invoice_number)

  if (!canVoidInvoices) {
    return { shipment, notice: `${invoiceName} är inte makulerad. Den makuleras på fakturasidan, om kunden inte ska betala den.` }
  }

  if (!dependencies.confirm(`${doneText} ${invoiceName} finns kvar. Vill du makulera den också?`)) {
    return { shipment, notice: `${invoiceName} står kvar. Den kan makuleras senare på fakturasidan.` }
  }

  try {
    await dependencies.voidInvoice(invoice.id)
  } catch {
    return { shipment, notice: `${invoiceName} kunde inte makuleras just nu. Försök igen på fakturasidan.` }
  }

  return { shipment, notice: `${invoiceName} är makulerad.` }
}

export async function writeOffShipmentWithInvoicePrompt(
  shipmentId: number,
  canVoidInvoices: boolean,
  dependencies: WriteOffDependencies
): Promise<WriteOffOutcome> {
  const result = await dependencies.writeOff(shipmentId)

  return askToVoidInvoice(result, WRITTEN_OFF_TEXT, canVoidInvoices, dependencies)
}

export async function returnShipmentWithInvoicePrompt(
  shipmentId: number,
  canVoidInvoices: boolean,
  dependencies: CustomerReturnDependencies
): Promise<WriteOffOutcome> {
  const result = await dependencies.returnShipment(shipmentId)

  return askToVoidInvoice(result, RETURNED_TEXT, canVoidInvoices, dependencies)
}
